import { createMiddleware } from '@shared/redux';
import { getObjectSelectedIsLoading } from '@domain/object-selected';
import { haveUserConfigAlreadyLoaded } from '@domain/user-config';
import { LOCATION_CHANGE } from 'connected-react-router';
import { getPathnameFromChoices } from '@domain/campaigns/campaigns.constants';
import { CHANGE_DASHBOARD_FILTER, SET_INITIAL_DASHBOARD_FILTER } from '@domain/dashboard/dashboard-filters';
import { UPDATE_OBJECT_SELECTED } from '@domain/_redux/pareto-ads-app-redux/action/actionTypes';
import { GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_SUCCEEDED, handleGetCampaignsReport } from '@domain/campaigns/campaigns.state';
import { onCampaignManagerRoute } from '@domain/campaigns/campaigns.utils';
import {
    getManagerReportNameParams,
    setDomainManagerParams,
} from '@domain/dashboard/dashboard-manager/dashboard-manager.state';
import { handleGetComparisonCampaigns } from '@domain/comparison-campaigns';
import { LOGGED_USER_REQUEST_SUCCEEDED } from '@domain/logged-user';
import {
    getMetricsPreviousReportParams,
    getMetricsReportParams,
    metricsReportCaller,
    previousPeriodMetricsReportCaller,
} from '@domain/dashboard/metrics-report';
import { getCampaignStatsParams } from '@domain/campaigns/campaigns.middlewares';
import { getComparisonCampaignStatsParams } from '@domain/comparison-campaigns/comparison-campaigns.middlewares';
import { SET_SELECTED_SEGMENT, SET_SELECTED_SEGMENT_BY_SLUG } from '@domain/segments';
import { isEqual } from 'lodash';
import { getCampaignsInfoParams } from '@domain/campaigns-info/campaigns-info.middleware';
import { handleGetCampaignsInfoReport } from '@domain/campaigns-info/campaigns-info.state';
import { getHandleGetClustersParams } from '@domain/omnichannel-budget-manager/clusters/clusters.middleware';
import { handleGetClusters } from '@domain/omnichannel-budget-manager/clusters';
import { handleGetTagGroupSegmentedReport } from '@domain/segments/tag-groups-segmented/tag-groups-segmented.state';
import { getTagGroupSegmentedReportParams } from '@domain/segments/tag-groups-segmented/tag-groups-segmented.utils';

const validateState = (state, action, reports) => {
    const objectSelectedAlreadyLoaded = !getObjectSelectedIsLoading(state);
    const userConfigAlreadyLoaded = haveUserConfigAlreadyLoaded(state);

    if (!objectSelectedAlreadyLoaded || !userConfigAlreadyLoaded) { return []; }

    const actions = reports.reduce((acc, reportDomain) => {
        const params = reportDomain.getParams(state);
        const oldParams = getManagerReportNameParams(state, reportDomain.reportName);
        if (!isEqual(params, oldParams)) {
            return [
                ...acc,
                setDomainManagerParams({
                    reportName: reportDomain.reportName,
                    params,
                }),
                reportDomain.action(),
            ];
        }
        return acc;
    }, []);
    return actions;
};

export const reportsDomains = {
    campaingsStats: {
        reportName: 'campaingsStats',
        action: handleGetCampaignsReport,
        getParams: getCampaignStatsParams,
    },
    budgetManagerCampaignsInfo: {
        reportName: 'budgetManagerCampaignsInfo',
        action: handleGetCampaignsInfoReport,
        getParams: getCampaignsInfoParams,
    },
    metricsReport: {
        reportName: 'metricsReport',
        action: metricsReportCaller,
        getParams: getMetricsReportParams,
    },
    metricsPreviousReport: {
        reportName: 'metricsPreviousReport',
        action: previousPeriodMetricsReportCaller,
        getParams: getMetricsPreviousReportParams,
    },
    comparisonCampaingsStats: {
        reportName: 'comparisonCampaingsStats',
        action: handleGetComparisonCampaigns,
        getParams: getComparisonCampaignStatsParams,
    },
    tagGroupSegmentedReport: {
        reportName: 'tagGroupSegmentedReport',
        action: handleGetTagGroupSegmentedReport,
        getParams: getTagGroupSegmentedReportParams,
    },
    getClientClusters: {
        reportName: 'getClientClusters',
        action: handleGetClusters,
        getParams: getHandleGetClustersParams,
    },
};

const validateAllReports = (state, action) => validateState(
    state, action, Object.values(reportsDomains),
);

export const dashboadManagerMiddleware = createMiddleware({
    [SET_INITIAL_DASHBOARD_FILTER]: onCampaignManagerRoute(
        getPathnameFromChoices.STATE, validateAllReports,
    ),
    [CHANGE_DASHBOARD_FILTER]: onCampaignManagerRoute(
        getPathnameFromChoices.STATE, validateAllReports,
    ),
    [LOCATION_CHANGE]: onCampaignManagerRoute(getPathnameFromChoices.ACTION, validateAllReports),
    [UPDATE_OBJECT_SELECTED]: onCampaignManagerRoute(
        getPathnameFromChoices.STATE, validateAllReports,
    ),
    [LOGGED_USER_REQUEST_SUCCEEDED]: onCampaignManagerRoute(
        getPathnameFromChoices.STATE, validateAllReports,
    ),
    [SET_SELECTED_SEGMENT_BY_SLUG]: onCampaignManagerRoute(
        getPathnameFromChoices.STATE,
        (state, action) => validateState(state, action, [reportsDomains.tagGroupSegmentedReport]),
    ),
    [SET_SELECTED_SEGMENT]: onCampaignManagerRoute(
        getPathnameFromChoices.STATE,
        (state, action) => validateState(state, action, [reportsDomains.tagGroupSegmentedReport]),
    ),
    [GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_SUCCEEDED]: onCampaignManagerRoute(
        getPathnameFromChoices.STATE,
        (state, action) => validateState(state, action, [reportsDomains.tagGroupSegmentedReport]),
    ),
});
